// Pencarian file & folder di seluruh drive (dipakai kolom Cari dan halaman
// hasil pencarian). Fungsi murni: daftar entri dibuat DisboxFileManager
// (searchIndex), di sini hanya mencocokkan dan mengurutkan.
//
// Aturan:
// - Semua kata yang diketik harus ada (di nama atau di folder letaknya),
//   urutan bebas, huruf besar/kecil dan aksen diabaikan.
// - "ext:mkv" (atau "*.mkv") membatasi ke ekstensi itu, bisa lebih dari satu.
// - Urutan hasil: nama persis sama, nama diawali kata itu, kata di awal
//   potongan nama, ada di nama, lalu yang cocok hanya karena nama foldernya.

export const SEARCH_TYPES = [
    { id: 'all', label: 'Semua' },
    { id: 'folder', label: 'Folder' },
    { id: 'video', label: 'Video' },
    { id: 'image', label: 'Gambar' },
    { id: 'audio', label: 'Audio' },
    { id: 'document', label: 'Dokumen' },
    { id: 'archive', label: 'Arsip' },
];

const TYPE_EXTENSIONS = {
    video: ['mp4', 'mkv', 'webm', 'mov', 'avi', 'm4v', 'ts', 'm2ts', 'flv', 'wmv', 'mpg', 'mpeg', '3gp'],
    image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'svg', 'tif', 'tiff', 'ico', 'psd', 'raw', 'cr2', 'nef'],
    audio: ['mp3', 'wav', 'flac', 'ogg', 'm4a', 'aac', 'opus', 'wma'],
    document: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'md', 'csv', 'odt', 'ods', 'odp', 'rtf', 'cdr', 'ai', 'eps', 'indd'],
    archive: ['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz', 'iso'],
};
const TYPE_OF_EXTENSION = {};
for (const [type, extensions] of Object.entries(TYPE_EXTENSIONS)) {
    for (const ext of extensions) TYPE_OF_EXTENSION[ext] = type;
}

export function normalize(text) {
    return String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function extensionOf(name) {
    const dot = String(name).lastIndexOf('.');
    return dot > 0 ? String(name).slice(dot + 1).toLowerCase() : '';
}

export function typeOfEntry(entry) {
    if (entry.type === 'directory') return 'folder';
    return TYPE_OF_EXTENSION[entry.ext] || 'other';
}

// "naruto ext:mkv *.mp4" -> { terms: ['naruto'], exts: ['mkv', 'mp4'] }
export function parseQuery(raw) {
    const terms = [];
    const exts = [];
    for (const word of normalize(raw).split(/\s+/)) {
        if (!word) continue;
        const ext = /^(?:ext:|\*\.)\.?([a-z0-9]+)$/.exec(word);
        if (ext) exts.push(ext[1]);
        else terms.push(word);
    }
    return { terms, exts };
}

export function isEmptyQuery(raw) {
    const { terms, exts } = parseQuery(raw);
    return terms.length === 0 && exts.length === 0;
}

// Huruf/angka sebelum posisi ini bukan bagian kata yang sama ("ep" di "Naruto_ep02").
const startsWord = (text, at) => at === 0 || !/[a-z0-9]/.test(text[at - 1]);

function scoreEntry(entry, terms, phrase) {
    let score = 0;
    for (const term of terms) {
        const at = entry.key.indexOf(term);
        if (at >= 0) {
            score += at === 0 ? 60 : startsWord(entry.key, at) ? 45 : 30;
        } else if (entry.parentKey.includes(term)) {
            score += 5;
        } else {
            return -1;
        }
    }
    if (phrase) {
        const base = entry.type === 'directory' ? entry.key : entry.key.slice(0, entry.key.length - (entry.ext ? entry.ext.length + 1 : 0));
        if (entry.key === phrase || base === phrase) score += 400;
        else if (entry.key.startsWith(phrase)) score += 200;
        else if (terms.length > 1 && entry.key.includes(phrase)) score += 100;
    }
    if (entry.type === 'directory') score += 8;
    // Nama pendek (lebih mirip yang dicari) sedikit di atas.
    return score - Math.min(entry.key.length, 200) / 50;
}

// entries: hasil DisboxFileManager.searchIndex().
// options: { type: id SEARCH_TYPES, within: path folder ('' / null = semua), limit }
// Mengembalikan { results, total, counts } - counts: jumlah hasil per jenis
// (sebelum filter jenis), untuk angka di tombol filter.
export function searchEntries(entries, raw, { type = 'all', within = null, limit = Infinity } = {}) {
    const { terms, exts } = parseQuery(raw);
    const counts = Object.fromEntries(SEARCH_TYPES.map((item) => [item.id, 0]));
    if (terms.length === 0 && exts.length === 0) return { results: [], total: 0, counts };
    const phrase = terms.join(' ');
    const prefix = within ? `${within}/` : null;
    const matched = [];
    for (const entry of entries) {
        if (prefix && !entry.path.startsWith(prefix)) continue;
        if (exts.length > 0 && (entry.type === 'directory' || !exts.includes(entry.ext))) continue;
        const score = terms.length > 0 ? scoreEntry(entry, terms, phrase) : 0;
        if (score < 0) continue;
        const entryType = typeOfEntry(entry);
        counts.all++;
        if (counts[entryType] !== undefined) counts[entryType]++;
        if (type !== 'all' && entryType !== type) continue;
        matched.push({ entry, score });
    }
    matched.sort((a, b) => b.score - a.score || a.entry.key.localeCompare(b.entry.key, undefined, { numeric: true }));
    const results = (limit < matched.length ? matched.slice(0, limit) : matched).map((item) => item.entry);
    return { results, total: matched.length, counts };
}

// Potongan nama untuk ditampilkan dengan kata yang cocok ditebalkan:
// [{ text, match }]. Panjang normalize() sama dengan aslinya untuk huruf
// Latin biasa; kalau tidak (huruf beraksen terurai), nama ditampilkan polos.
export function highlightParts(name, raw) {
    const text = String(name || '');
    const key = normalize(text);
    const { terms } = parseQuery(raw);
    if (terms.length === 0 || key.length !== text.length) return [{ text, match: false }];
    const marks = new Array(text.length).fill(false);
    for (const term of terms) {
        let at = key.indexOf(term);
        while (at >= 0) {
            for (let i = at; i < at + term.length; i++) marks[i] = true;
            at = key.indexOf(term, at + term.length);
        }
    }
    const parts = [];
    for (let i = 0; i < text.length; i++) {
        const last = parts[parts.length - 1];
        if (last && last.match === marks[i]) last.text += text[i];
        else parts.push({ text: text[i], match: marks[i] });
    }
    return parts;
}
